/**
 * Gestion des permissions front-end.
 *
 * NOTE : ce gating est uniquement côté interface (UX). Il masque des éléments
 * sensibles (ex: le system prompt brut) mais ne constitue pas un verrou de
 * sécurité — l'API reste techniquement accessible.
 *
 * Robustesse : selon la méthode de connexion (email/password vs Google),
 * l'email n'est pas toujours présent dans le localStorage. L'ID owner, lui,
 * est toujours disponible (dans data.id / data.userId, ou dans le token JWT).
 * On autorise donc par EMAIL **ou** par ID owner.
 */

// Superadmins autorisés à voir/éditer le system prompt brut
export const SUPERADMIN_EMAILS = [
  'mamadou.dieye@peelo.chat',
  'ardo.sow@didasco.co'
];

// IDs owner des superadmins (fiables quel que soit le mode de connexion)
export const SUPERADMIN_IDS = [
  '67f69571e6bda8cc4e28fda4' // mamadou.dieye@peelo.chat (Mamadou Dieye)
  // ardo.sow@didasco.co : ajouter son _id owner dès qu'il a un compte
];

function getUserObj() {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
}

/** Décode (sans vérifier) le payload d'un JWT et renvoie l'objet, ou null. */
function decodeJwtPayload(token) {
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json);
  } catch (e) {
    return null;
  }
}

/**
 * Email de l'utilisateur connecté (cherché à plusieurs emplacements possibles).
 * @returns {string|null}
 */
export function getCurrentUserEmail() {
  const u = getUserObj();
  if (!u) return null;
  let email =
    u?.data?.email ||
    u?.email ||
    u?.data?.user?.email ||
    u?.data?.owner?.email ||
    null;

  // Fallback : le token JWT est signé avec l'objet owner complet (email inclus)
  if (!email) {
    const token = u?.data?.token || u?.token;
    if (token) {
      const payload = decodeJwtPayload(token);
      email = payload?.email || null;
    }
  }
  return email ? String(email).trim().toLowerCase() : null;
}

/**
 * ID owner de l'utilisateur connecté. Cherche dans les champs connus, puis
 * décode le token JWT (payload._id) en dernier recours.
 * @returns {string|null}
 */
export function getCurrentUserId() {
  const u = getUserObj();
  if (!u) return null;

  const direct =
    u?.data?.id ||
    u?.data?._id ||
    u?.data?.userId ||
    u?.data?.owner?._id ||
    u?.id ||
    null;
  if (direct) return String(direct);

  const token = u?.data?.token || u?.token;
  if (token) {
    const payload = decodeJwtPayload(token);
    if (payload && (payload._id || payload.id || payload.userId)) {
      return String(payload._id || payload.id || payload.userId);
    }
  }
  return null;
}

/**
 * Indique si l'utilisateur connecté est un superadmin (par email OU par ID owner).
 * @returns {boolean}
 */
export function isSuperAdmin() {
  const email = getCurrentUserEmail();
  if (email && SUPERADMIN_EMAILS.map(e => e.toLowerCase()).includes(email)) {
    return true;
  }
  const id = getCurrentUserId();
  if (id && SUPERADMIN_IDS.includes(id)) {
    return true;
  }
  return false;
}
